import { isEmbedded } from "./Utils";
const ENTRY_SCRIPT_PATTERNS = [/app\.[^/]+\.js$/, /chunk-vendors\.[^/]+\.js$/];
const EMBEDDED_IFRAME_BASE_PATH = '/fcr/';
const EMBEDDED_PROXY_BASE_PATH = '/consolidation/';
let cachedBasePath: string | null = null;

export function detectBasePath(): string {
    if (cachedBasePath) {
        return cachedBasePath;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        cachedBasePath = '/';
        return cachedBasePath;
    }

    if (isEmbedded()) {
        cachedBasePath = EMBEDDED_PROXY_BASE_PATH;
        return cachedBasePath;
    }

    const baseFromScript = getBasePathFromScripts();
    if (baseFromScript) {
        cachedBasePath = baseFromScript;
        return cachedBasePath;
    }

    cachedBasePath = '/';
    return cachedBasePath;
}

export function resolveWithBase(relativePath: string): string {
    const basePath = detectBasePath();
    const sanitized = normalizeRelativePath(relativePath.startsWith('/') ? relativePath.slice(1) : relativePath);
    if (!sanitized) {
        return basePath;
    }

    const resolvedPath = `${basePath}${sanitized}`.replace(/\/+/g, '/');
    return resolvedPath;
}

export function resolveDeeplink(relativePath: string): string {
    if (!isEmbedded()) {
        return relativePath;
    }

    if (!relativePath.startsWith(EMBEDDED_PROXY_BASE_PATH)) {
        return relativePath;
    }

    return EMBEDDED_IFRAME_BASE_PATH + relativePath.slice(EMBEDDED_PROXY_BASE_PATH.length);
}

function normalizeRelativePath(path: string): string {
    if (!path) {
        return '';
    }

    const segments = path.split('/');
    const resolved: string[] = [];

    for (const segment of segments) {
        if (!segment || segment === '.') {
            continue;
        }

        if (segment === '..') {
            resolved.pop();
            continue;
        }

        resolved.push(segment);
    }

    return resolved.join('/');
}

function getBasePathFromScripts(): string | null {
    const scripts = Array.from(document.getElementsByTagName('script')) as HTMLScriptElement[];
    if (!scripts.length) {
        return null;
    }

    const preferred = scripts.find(script => script.src && ENTRY_SCRIPT_PATTERNS.some(pattern => pattern.test(script.src)));
    const candidate = preferred ?? scripts.find(script => Boolean(script.src)) ?? scripts[scripts.length - 1];

    if (!candidate || !candidate.src) {
        return null;
    }

    const url = new URL(candidate.src, window.location.href);
    return normalizeBasePath(stripFilename(url.pathname));
}

function stripFilename(pathname: string): string {
    if (!pathname) {
        return '/';
    }

    const normalized = pathname.endsWith('/') ? pathname : pathname.substring(0, pathname.lastIndexOf('/') + 1);
    return normalized || '/';
}

function normalizeBasePath(path: string | null | undefined): string {
    if (!path) {
        return '/';
    }

    let normalized = path;

    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        const url = new URL(normalized);
        normalized = url.pathname;
    }

    if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
    }

    normalized = normalized.replace(/\/+/g, '/');

    if (!normalized.endsWith('/')) {
        normalized = `${normalized}/`;
    }

    if (normalized === '//') {
        normalized = '/';
    }

    return normalized;
}

declare global {
    interface Window {
        __FCR_BASE_PATH__?: string;
    }
}
